import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SupportField, SupportOption, SupportStepDefinition, SupportStepPayload } from '../../types/chat';

interface SupportFlowWidgetProps {
    payload: SupportStepPayload;
    onSubmit: (payload: { stepId: string; answer: any }) => Promise<void> | void;
    submitting?: boolean;
    onCancel?: () => void;
}

interface OptionState {
    selected: Set<string>;
    otherValue: string;
}

const SupportFlowWidget: React.FC<SupportFlowWidgetProps> = ({ payload, onSubmit, submitting = false, onCancel }) => {
    const step = payload.step;
    const currentStepIndex = payload.step_index || 1;
    const totalSteps = payload.total_steps || undefined;
    const prefill = payload.responses?.[step.id];

    const [selectedOption, setSelectedOption] = useState<string>(typeof prefill === 'string' ? prefill : '');
    const [multiState, setMultiState] = useState<OptionState>(() => {
        const initial = new Set<string>();
        let otherValue = '';
        const validValues = new Set((step.options || []).map(opt => opt.value));
        if (Array.isArray(prefill)) {
            prefill.forEach((value: string) => {
                if (!value) return;
                if (validValues.has(value)) {
                    initial.add(value);
                } else if (step.allow_other) {
                    initial.add('other');
                    if (!otherValue) {
                        otherValue = value;
                    }
                }
            });
        }
        return { selected: initial, otherValue };
    });
    const [textValue, setTextValue] = useState<string>(typeof prefill === 'string' ? prefill : '');
    const [contactValue, setContactValue] = useState<Record<string, string>>(() => {
        if (typeof prefill === 'object' && prefill !== null) {
            return { ...prefill };
        }
        return {};
    });
    const [error, setError] = useState<string | null>(null);
    const [fileUploads, setFileUploads] = useState<File[]>([]);
    const [filePreviews, setFilePreviews] = useState<string[]>([]);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const autoSubmitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const isOtherSelected = useMemo(() => multiState.selected.has('other'), [multiState.selected]);

    useEffect(() => {
        return () => {
            if (autoSubmitTimeoutRef.current) {
                clearTimeout(autoSubmitTimeoutRef.current);
            }
        };
    }, []);

    const handleSingleSelect = (value: string) => {
        setSelectedOption(value);
        setError(null);

        if (!submitting) {
            if (autoSubmitTimeoutRef.current) {
                clearTimeout(autoSubmitTimeoutRef.current);
            }
            autoSubmitTimeoutRef.current = setTimeout(() => {
                handleSubmitStep();
            }, 120);
        }
    };

    const handleMultiToggle = (value: string) => {
        setMultiState(prev => {
            const next = new Set(prev.selected);
            if (next.has(value)) {
                next.delete(value);
                if (value === 'other') {
                    return { selected: next, otherValue: '' };
                }
            } else {
                next.add(value);
            }
            return { ...prev, selected: next };
        });
        setError(null);
    };

    const handleMultiOtherChange = (value: string) => {
        setMultiState(prev => ({ ...prev, otherValue: value }));
    };

    const handleContactChange = (field: SupportField, value: string) => {
        setContactValue(prev => ({ ...prev, [field.id]: value }));
        setError(null);
    };

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files) return;

        const maxSize = (step.max_size_mb || 10) * 1024 * 1024; // Convert MB to bytes
        const newFiles: File[] = [];
        const newPreviews: string[] = [];

        for (let i = 0; i < files.length; i++) {
            const file = files[i];

            // Check file size
            if (file.size > maxSize) {
                setError(`File "${file.name}" exceeds maximum size of ${step.max_size_mb || 10}MB`);
                continue;
            }

            // Check file type if specified
            if (step.accept && step.accept !== 'image/*') {
                const acceptedTypes = step.accept.split(',').map(t => t.trim());
                const fileType = file.type || '';
                const fileExt = '.' + file.name.split('.').pop()?.toLowerCase();

                const isAccepted = acceptedTypes.some(accept => {
                    if (accept.startsWith('.')) {
                        return fileExt === accept.toLowerCase();
                    }
                    return fileType.startsWith(accept.replace('*', ''));
                });

                if (!isAccepted) {
                    setError(`File "${file.name}" is not an accepted type`);
                    continue;
                }
            }

            newFiles.push(file);

            // Create preview for images
            if (file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    const result = e.target?.result;
                    if (result) {
                        setFilePreviews(prev => [...prev, result as string]);
                    }
                };
                reader.readAsDataURL(file);
            }
        }

        if (step.multiple) {
            setFileUploads(prev => [...prev, ...newFiles]);
        } else {
            setFileUploads(newFiles.slice(0, 1));
            setFilePreviews([]); // Clear previous previews for single file
        }

        setError(null);
    };

    const handleRemoveFile = (index: number) => {
        setFileUploads(prev => prev.filter((_, i) => i !== index));
        setFilePreviews(prev => prev.filter((_, i) => i !== index));
    };

    const renderOptions = (options: SupportOption[] = []) => (
        <div className="flex flex-col space-y-2">
            {options.map(option => (
                <button
                    key={option.value}
                    type="button"
                    disabled={submitting}
                    onClick={() => handleSingleSelect(option.value)}
                    className={`rounded-lg border px-4 py-3 text-left transition hover:border-brand-500 ${
                        selectedOption === option.value ? 'border-brand-500 bg-brand-50 text-brand-600' : 'border-gray-200 bg-white'
                    }`}
                >
                    {option.label}
                </button>
            ))}
        </div>
    );

    const renderMultiSelect = (options: SupportOption[] = []) => (
        <div className="space-y-3">
            {options.map(option => {
                const checked = multiState.selected.has(option.value);
                return (
                    <label key={option.value} className={`flex items-start space-x-3 rounded-lg border px-4 py-3 transition hover:border-brand-500 ${
                        checked ? 'border-brand-500 bg-brand-50' : 'border-gray-200 bg-white'
                    }`}>
                        <input
                            type="checkbox"
                            checked={checked}
                            disabled={submitting}
                            onChange={() => handleMultiToggle(option.value)}
                            className="mt-1 h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                        />
                        <span className="text-sm text-gray-700">{option.label}</span>
                    </label>
                );
            })}
            {step.allow_other && multiState.selected.has('other') && (
                <div className="rounded-lg border border-brand-200 bg-brand-50 p-3">
                    <label className="text-xs font-semibold text-brand-700">Tell us more</label>
                    <textarea
                        className="mt-1 w-full rounded-md border border-brand-300 bg-white p-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                        rows={3}
                        value={multiState.otherValue}
                        disabled={submitting}
                        onChange={e => handleMultiOtherChange(e.target.value)}
                        placeholder="Describe what else you're seeing"
                    />
                </div>
            )}
        </div>
    );

    const renderTextInput = (multiline: boolean) => (
        <div className="space-y-2">
            {multiline ? (
                <textarea
                    className="w-full rounded-lg border border-gray-300 p-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                    rows={6}
                    placeholder={step.placeholder || 'Share the details'}
                    value={textValue}
                    disabled={submitting}
                    onChange={e => {
                        setTextValue(e.target.value);
                        setError(null);
                    }}
                />
            ) : (
                <input
                    type="text"
                    className="w-full rounded-lg border border-gray-300 p-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                    placeholder={step.placeholder || ''}
                    value={textValue}
                    disabled={submitting}
                    onChange={e => {
                        setTextValue(e.target.value);
                        setError(null);
                    }}
                />
            )}
        </div>
    );

    const renderImageUpload = () => (
        <div className="space-y-4">
            <div
                className="relative border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-brand-400 transition cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept={step.accept || 'image/*'}
                    multiple={step.multiple || false}
                    onChange={handleFileUpload}
                    disabled={submitting}
                />

                <div className="space-y-2">
                    <div className="mx-auto h-12 w-12 text-gray-400">
                        <svg className="h-full w-full" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                            <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                    <div className="text-sm text-gray-600">
                        <span className="font-semibold text-brand-500">Click to upload</span> or drag and drop
                    </div>
                    <p className="text-xs text-gray-500">
                        {step.accept ? `Accepted types: ${step.accept}` : 'Any image file'}
                        {step.max_size_mb && ` • Max size: ${step.max_size_mb}MB`}
                        {step.multiple && ' • Multiple files allowed'}
                    </p>
                </div>
            </div>

            {fileUploads.length > 0 && (
                <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-700">Uploaded files:</p>
                    <div className={`grid gap-2 ${filePreviews.length > 0 ? 'grid-cols-2 md:grid-cols-3' : 'grid-cols-1'}`}>
                        {fileUploads.map((file, index) => (
                            <div key={index} className="relative group">
                                {filePreviews[index] ? (
                                    <div className="relative rounded-lg overflow-hidden border border-gray-200">
                                        <img
                                            src={filePreviews[index]}
                                            alt={file.name}
                                            className="w-full h-32 object-cover"
                                        />
                                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition flex items-center justify-center">
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveFile(index)}
                                                className="opacity-0 group-hover:opacity-100 bg-red-500 text-white p-2 rounded-full transition"
                                                disabled={submitting}
                                            >
                                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </div>
                                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 truncate">
                                            {file.name}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 bg-gray-50">
                                        <div className="flex items-center gap-2 flex-1 min-w-0">
                                            <svg className="h-5 w-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                            <span className="text-sm text-gray-700 truncate">{file.name}</span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveFile(index)}
                                            className="text-red-500 hover:text-red-700 ml-2"
                                            disabled={submitting}
                                        >
                                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );

    const renderContactFields = (fields: SupportField[] = []) => (
        <div className="space-y-3">
            {fields.map(field => (
                <div key={field.id} className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">
                        {field.label}
                        {field.required && <span className="ml-1 text-red-500">*</span>}
                    </label>
                    <input
                        type={field.type === 'phone' ? 'tel' : field.type === 'number' ? 'number' : field.type}
                        className="w-full rounded-lg border border-gray-300 p-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                        placeholder={field.placeholder || ''}
                        value={contactValue[field.id] || ''}
                        disabled={submitting}
                        onChange={e => handleContactChange(field, e.target.value)}
                    />
                </div>
            ))}
        </div>
    );

    const handleSubmitStep = async () => {
        try {
            setError(null);
            let answer: any;

            switch (step.type) {
                case 'single_select':
                    if (!selectedOption && step.required) {
                        setError('Please choose an option to continue');
                        return;
                    }
                    answer = selectedOption;
                    break;
                case 'multi_select':
                    if (step.required && multiState.selected.size === 0) {
                        setError('Pick at least one option to continue');
                        return;
                    }
                    if (step.allow_other && multiState.selected.has('other') && !multiState.otherValue.trim()) {
                        setError('Please describe the "Other" option.');
                        return;
                    }
                    answer = Array.from(multiState.selected);
                    if (step.allow_other && multiState.selected.has('other')) {
                        answer = answer.map((value: string) => (value === 'other' ? multiState.otherValue.trim() || 'other' : value));
                    }
                    break;
                case 'text':
                case 'long_text':
                    if (step.required && !textValue.trim()) {
                        setError('Tell us a bit more so we can help');
                        return;
                    }
                    if (step.min_length && textValue.trim().length < step.min_length) {
                        setError(`Please provide at least ${step.min_length} characters.`);
                        return;
                    }
                    answer = textValue.trim();
                    break;
                case 'contact':
                    for (const field of step.fields || []) {
                        const value = (contactValue[field.id] || '').trim();
                        if (field.required && !value) {
                            setError(`${field.label} is required`);
                            return;
                        }
                        if (field.type === 'email' && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                            setError('Enter a valid email address');
                            return;
                        }
                    }
                    answer = contactValue;
                    break;
                case 'image':
                    if (step.required && fileUploads.length === 0) {
                        setError('Please upload at least one image');
                        return;
                    }
                    // Convert files to base64 for transmission
                    const fileData = await Promise.all(
                        fileUploads.map(async (file) => {
                            const base64 = await new Promise<string>((resolve, reject) => {
                                const reader = new FileReader();
                                reader.onload = () => resolve(reader.result as string);
                                reader.onerror = reject;
                                reader.readAsDataURL(file);
                            });
                            return {
                                name: file.name,
                                type: file.type,
                                size: file.size,
                                data: base64
                            };
                        })
                    );
                    answer = fileData;
                    break;
                default:
                    answer = textValue;
            }

        await onSubmit({ stepId: step.id, answer });
    } catch (err: any) {
        console.error('Support flow submission failed:', err);
        setError(err?.message || 'Something went wrong. Please try again.');
    }
};

    const renderStepBody = () => {
        switch (step.type) {
            case 'single_select':
                return renderOptions(step.options);
            case 'multi_select':
                return renderMultiSelect(step.options);
            case 'text':
                return renderTextInput(false);
            case 'long_text':
                return renderTextInput(true);
            case 'contact':
                return renderContactFields(step.fields);
            case 'image':
                return renderImageUpload();
            default:
                return renderTextInput(true);
        }
    };

    return (
        <div className="rounded-2xl border border-brand-200 bg-white p-5 shadow-sm">
            <div className="mb-4">
                {payload.flow_title && (
                    <p className="text-xs uppercase tracking-wide text-brand-500">{payload.flow_title}</p>
                )}
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">{step.title}</h3>
                    {totalSteps && (
                        <span className="text-xs font-medium uppercase tracking-wide text-brand-500">
                            Step {currentStepIndex} of {totalSteps}
                        </span>
                    )}
                </div>
                {step.description && (
                    <p className="mt-1 text-sm text-gray-600">{step.description}</p>
                )}
            </div>

            <div className="space-y-4">
                {renderStepBody()}

                {error && (
                    <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                        {error}
                    </div>
                )}

                <div className="flex items-center justify-end space-x-3">
                    {onCancel && (
                        <button
                            type="button"
                            onClick={onCancel}
                            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
                            disabled={submitting}
                        >
                            Cancel
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={handleSubmitStep}
                        disabled={submitting}
                        className="rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-400"
                    >
                        {submitting ? 'Sending…' : 'Continue'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SupportFlowWidget;
